import axios from 'axios';
import common from '../jsons/common.json'
import { db } from '../db';

const valueHelper = {

    isDateInPast(date) {
        let newDate = new Date(date);
        return newDate < new Date();
    },

    valueSelect(value, options) {
        let selected = null;
        if (options != null) {
            if (value != null) {
                selected = options.find(x => x.value === value);
            } else {
                selected = options.find(x => x.default === true);
            }
        }
        return selected;
    },

    async getCountries() {
        let raw = await getItem('countries');
        let json = null;
        if (raw == null) {
            let res = await axios.get('/api/country/select');
            await setItem('countries', res.data);
            json = res.data;
        } else {
            json = raw.data;
        }
        return json;
    },

    async getTimezones() {
        // Static and tenant-independent. Bump the key when the supported country list changes.
        return await getValues(null, 'timezones-v2', 'value/timezones', null);
    },

    async getCanadianProvs() {
        let raw = await getItem('canadianProvs');
        let json = null;
        if (raw == null) {
            let res = await axios.get('/api/province/select?country=' + common.canada);
            await setItem('canadianProvs', res.data);
            json = res.data;
        } else {
            json = raw.data;
        }
        return json;
    },

    async getPermCachedValues(list, tenant) {
        let result = {};
        for (let row of list) {
            let values = null;
            switch (row) {
                case common.cacheValues.editReason:
                    values = await getValues(null, 'editReasons', 'value/bytype/' + common.valueTypes.editReason.id, tenant);
                    result.editReasons = values;
                    break;
                default:
                    break;
            }

        }
        return result;
    },

    // Equipment for one machine type. Cached per tenant and invalidated off
    // lastUpdated.equipment, the same freshness signal the other equipment
    // caches (equipmentRecords, equipmentAllRecords, ...) use.
    async getEquipmentByMachineType(machineType, tenant) {
        const ten = tenant ?? currentTenant();
        const dateValues = await tenantUpdatedDates();
        return await getValues(dateValues.equipment, 'equipment' + machineType,
            'equipment/select?machineType=' + machineType, ten);
    },

    getValueTypeKeyFromId(findId) {
        for (let key in common.valueTypes) {
            if (common.valueTypes[key].id === findId) {
                return common.valueTypes[key].valueName;
            }
        }
    },

    inList: function (type, types) {
        let found = types.find(x => x === type);
        return found != null;
    },

    packagedHCCategory(category) {
        if (category == null || category === '')
            return false;
        let found = common.packagedHCCategories.find(x => x === category);
        return found != null;
    },

    thcHCCategory(category) {
        if (category == null || category === '')
            return false;
        let found = common.thcHCCategories.find(x => x === category);
        return found != null;
    },

    stageItemSet(stage, items, constants) {
        let dryIndex = items.findIndex(x => x.value === constants.items.dryPlantWaste);
        if (dryIndex > -1) {
            items.splice(dryIndex, 1);
        }
        let exciseIndex = items.findIndex(x => x.value === constants.items.exciseWaste);
        if (exciseIndex > -1) {
            items.splice(exciseIndex, 1);
        }
        let hempIndex = items.findIndex(x => x.value === constants.items.hempWaste);
        if (hempIndex > -1) {
            items.splice(hempIndex, 1);
        }
        let rootIndex = items.findIndex(x => x.value === constants.items.rootball);
        if (rootIndex > -1) {
            if (stage === constants.batchStages.germination || stage === constants.batchStages.prop) {
                items.splice(rootIndex, 1);
            }
        }
        let vegIndex = items.findIndex(x => x.value === constants.items.vegPlantWaste);
        if (vegIndex > -1) {
            if (stage !== constants.batchStages.prop && stage !== constants.batchStages.veg && stage !== constants.batchStages.mother) {
                items.splice(vegIndex, 1);
            }
        }
        let wholeIndex = items.findIndex(x => x.value === constants.items.wholePlantWaste);
        if (wholeIndex > -1) {
            if (stage !== constants.batchStages.flower) {
                items.splice(wholeIndex, 1);
            }
        }
        let wetIndex = items.findIndex(x => x.value === constants.items.wetPlantWaste);
        if (wetIndex > -1) {
            if (stage !== constants.batchStages.harvest && stage !== constants.batchStages.harvestWaste) {
                items.splice(wetIndex, 1);
            }
        }
        let seedIndex = items.findIndex(x => x.value === constants.items.seedWaste);
        if (seedIndex > -1) {
            if (stage !== constants.batchStages.germination) {
                items.splice(seedIndex, 1);
            }
        }
        return items;
    },

    async getStatusesForObject(objectId, tenant) {
        let dateRes = await axios.get('/api/tenantupdateddates');
        let dateValues = dateRes.data;
        let dataObj = await getItem(objectId + 'Statuses' + tenant);
        let json = dataObj != null ? dataObj.data : null;

        let cacheDateRaw = dataObj != null ? dataObj.date : null;
        let retrieve = false
        if (json == null) {
            retrieve = true;
        }
        if (!retrieve && cacheDateRaw != null) {
            retrieve = serverNewer(dateValues.value, cacheDateRaw);
        }
        if (retrieve) {
            let res = await axios.get('/api/object/statuses/' + objectId);
            await setItem(objectId + 'Statuses' + tenant, res.data);
            json = res.data;
        }
        return json;
    },

    async getUserSignature(id) {
        let data = null;
        if (id != null && id !== '') {
            const key = 'signature-' + id;
            let cached = await getItem(key);
            if (cached != null) {
                data = cached.data;
            } else {
                let result = await axios.get('/api/usersignature/' + id);
                data = result.data;
                await setItem(key, data);
            }
        }
        return data;
    },

    async getCachedValues(list, constants, objectId, tenant) {
        let dateRes = tenant != null  ? await axios.get('/api/tenantupdateddates', { params: { tenant: tenant } }) : {};
        let dateValues = dateRes?.data || {};
        let result = {};
        for (let row of list) {
            let values = null;
            switch (row) {
                case common.cacheValues.object:
                    values = await getValues(dateValues.object, 'allObjects', 'object/select', tenant);
                    result.objects = values;
                    break;
                case common.cacheValues.itemLedgerObject:
                    values = await getValues(dateValues.object, 'itemLedgerObjects', 'object/select?canItemLedger=true', tenant);
                    result.itemLedgerObjects = values;
                    break;
                case common.cacheValues.workRecordObject:
                    values = await getValues(dateValues.object, 'workRecordObjects', 'object/select?canWorkRecord=true', tenant);
                    result.workRecordObjects = values;
                    break;
                case common.cacheValues.item:
                    values = await getValues(dateValues.item, 'selectItems', 'item/availableitems', tenant);
                    result.items = values;
                    break;
                case common.cacheValues.allItems:
                    values = await getValues(dateValues.item, 'allItems', 'item/all', tenant);
                    result.allItems = values;
                    break;
                case common.cacheValues.activeUser:
                    values = await getValues(dateValues.user, 'activeUsers', 'user/active', tenant);
                    result.activeUsers = values;
                    break;
                case common.cacheValues.user:
                    values = await getValues(dateValues.user, 'allUsers', 'user/all', tenant);
                    result.users = values;
                    break;
                case common.cacheValues.manager:
                    values = await getValues(dateValues.user, 'allManagers', 'userlist/managers', tenant);
                    result.managers = values;
                    break;
                case common.cacheValues.qa:
                    values = await getValues(dateValues.user, 'allQas', 'userlist/qas', tenant);
                    result.qas = values;
                    break;
                case common.cacheValues.bin:
                    values = await getValues(dateValues.bin, 'allBins', 'bin/select', tenant);
                    result.bins = values;
                    break;
                case common.cacheValues.defaultBin:
                    values = await getValues(dateValues.bin, 'defaultBins', 'bin/selectdefault', tenant);
                    result.defaultBins = values;
                    break;
                case common.cacheValues.testDefinition:
                    values = await getValues(dateValues.testDefinition, 'testDefinitions', 'testdefinition/select', tenant);
                    result.testDefinitions = values;
                    break;
                case common.cacheValues.customer:
                    values = await getValues(dateValues.customer, 'allCustomers', 'customer/select', tenant);
                    result.customers = values;
                    break;
                case common.cacheValues.vendor:
                    values = await getValues(dateValues.vendor, 'allVendors', 'vendor/select', tenant);
                    result.vendors = values;
                    break;
                case common.cacheValues.maintenanceVendor:
                    values = await getValues(dateValues.vendor, 'maintenanceVendor', 'vendor/select?type=' + constants.vendorTypes.maintenance, tenant);
                    result.maintenanceVendors = values;
                    break;
                case common.cacheValues.cropTreatmentVendor:
                    values = await getValues(dateValues.vendor, 'cropTreatmentVendor', 'vendor/select?type=' + constants.vendorTypes.cropTreatment, tenant);
                    result.cropTreatmentVendors = values;
                    break;
                case common.cacheValues.shippingVendor:
                    values = await getValues(dateValues.vendor, 'shippingVendor', 'vendor/select?type=' + constants.vendorTypes.shipping, tenant);
                    result.shippingVendors = values;
                    break;
                case common.cacheValues.equipment:
                    values = await getValues(dateValues.equipment, 'equipmentRecords', 'equipment/select', tenant);
                    result.equipments = values;
                    break;
                case common.cacheValues.equipmentAll:
                    values = await getValues(dateValues.equipment, 'equipmentAllRecords', 'equipment/select?hide=true', tenant);
                    result.equipmentAll = values;
                    break;
                case common.cacheValues.processingEquipment:
                    values = await getValues(dateValues.equipment, 'processingEquipments', 'equipment/select', tenant);
                    result.processingEquipments = values;
                    break;
                case common.cacheValues.strain:
                    values = await getValues(dateValues.strain, 'allStrains', 'strain/select', tenant);
                    result.strains = values;
                    break;
                case common.cacheValues.cultivationBatch:
                    values = await getValues(dateValues.cultivationBatch, 'allCultivationBatches', 'cultivationbatch/select', tenant);
                    result.batches = values;
                    break;
                case common.cacheValues.pesticideRecipe:
                    values = await getValues(dateValues.pesticideRecipe, 'allPesticideRecipes', 'pesticiderecipe/select', tenant);
                    result.pesticideRecipes = values;
                    break;
                case common.cacheValues.itemCategory:
                    values = await getValues(dateValues.itemCategory, 'allItemCategories', 'itemcatgory/select', tenant);
                    result.itemCategories = values;
                    break;
                case common.cacheValues.status:
                    values = await getValues(dateValues.status, 'allStatuses', 'value/bytype/' + common.valueTypes.status.id, tenant);
                    result.statuses = values;
                    break;
                case common.cacheValues.hcReportCategory:
                    values = await getValues(dateValues.hcReportCategory, 'allHcReportCategory', 'value/bytype/' + common.valueTypes.hcReportCategory.id, tenant);
                    result.hcReportCategories = values;
                    break;
                case common.cacheValues.productionOrderStatus:
                    values = await getValues(dateValues.productionOrderStatus, 'allProductionOrderStatuses', 'value/bytype/' + common.valueTypes.productionOrderStatus.id, tenant);
                    result.productionOrderStatuses = values;
                    break;
                case common.cacheValues.routing:
                    values = await getValues(dateValues.routing, 'allRoutings', 'routing/select', tenant);
                    result.routings = values;
                    break;
                case common.cacheValues.routingAll:
                    values = await getValues(dateValues.routing, 'allRoutingsHidden', 'routing/select?all=true', tenant);
                    result.allRoutings = values;
                    break;
                case common.cacheValues.bom:
                    values = await getValues(dateValues.bom, 'allBoms', 'bom/select', tenant);
                    result.boms = values;
                    break;
                case common.cacheValues.bomAll:
                    values = await getValues(dateValues.bom, 'allBomsHidden', 'bom/select?all=true', tenant);
                    result.allBoms = values;
                    break;
                case common.cacheValues.equipmentCalibrationType:
                    values = await getValues(dateValues.equipmentCalibrationType, 'allEquipmentCalibrationTypes', 'equipmentcalibtype/select', tenant);
                    result.equipmentCalibrationTypes = values;
                    break;
                case common.cacheValues.department:
                    values = await getValues(dateValues.value, 'allDepartments', 'value/bytype/' + common.valueTypes.department.id, tenant);
                    result.departments = values;
                    break;
                case common.cacheValues.role:
                    values = await getValues(dateValues.value, 'allRoles', 'value/bytype/' + common.valueTypes.role.id, tenant);
                    result.roles = values;
                    break;
                case common.cacheValues.businessProcess:
                    values = await getValues(dateValues.value, 'allBusinessProcesses', 'value/bytype/' + common.valueTypes.businessProcess.id, tenant);
                    result.processes = values;
                    break;
                case common.cacheValues.capaType:
                    values = await getValues(dateValues.value, 'allCapaTypes', 'value/bytype/' + common.valueTypes.capaType.id, tenant);
                    result.capaTypes = values;
                    break;
                case common.cacheValues.areaUOM:
                    values = await getValues(dateValues.value, 'allAreaUOMs', 'value/bytype/' + common.valueTypes.areaUOM.id, tenant);
                    result.areaUOMs = values;
                    break;
                case common.cacheValues.quantityUOM:
                    values = await getValues(dateValues.value, 'allQuantityUOMs', 'value/bytype/' + common.valueTypes.quantityUom.id, tenant);
                    result.quantityUOMs = values;
                    break;
                case common.cacheValues.timeUOM:
                    values = await getValues(dateValues.value, 'allTimeUOMs', 'value/bytype/' + common.valueTypes.timeUom.id, tenant);
                    result.timeUOMs = values;
                    break;
                case common.cacheValues.batchStatus:
                    values = await getValues(dateValues.value, 'allBatchStatuses', 'value/bytype/' + common.valueTypes.batchStatus.id, tenant);
                    result.batchStatuses = values;
                    break;
                case common.cacheValues.batchStage:
                    values = await getValues(dateValues.value, 'allBatchStages', 'value/bytype/' + common.valueTypes.batchStage.id, tenant);
                    result.batchStages = values;
                    break;
                case common.cacheValues.containerType:
                    values = await getValues(dateValues.value, 'allContainerTypes', 'value/bytype/' + common.valueTypes.containerType.id, tenant);
                    result.containerTypes = values;
                    break;
                case common.cacheValues.lotStatus:
                    values = await getValues(dateValues.value, 'allLotStatuses', 'value/bytype/' + common.valueTypes.lotStatus.id, tenant);
                    result.lotStatuses = values;
                    break;
                case common.cacheValues.containerStatus:
                    values = await getValues(dateValues.value, 'allContainerStatuses', 'value/bytype/' + common.valueTypes.containerStatus.id, tenant);
                    result.containerStatuses = values;
                    break;
                case common.cacheValues.productionBatchStatus:
                    values = await getValues(dateValues.value, 'allProductionBatchStatuses', 'value/bytype/' + common.valueTypes.productionBatchStatus.id, tenant);
                    result.productionBatchStatuses = values;
                    break;
                case common.cacheValues.pest:
                    values = await getValues(dateValues.value, 'allPests', 'value/bytype/' + common.valueTypes.pest.id, tenant);
                    result.pests = values;
                    break;
                case common.cacheValues.roomType:
                    values = await getValues(dateValues.value, 'allRoomtypes', 'value/bytype/' + common.valueTypes.roomType.id, tenant);
                    result.roomTypes = values;
                    break;
                case common.cacheValues.lightType:
                    values = await getValues(dateValues.value, 'allLightTypes', 'value/bytype/' + common.valueTypes.lightType.id, tenant);
                    result.lightTypes = values;
                    break;
                case common.cacheValues.adjustmentReason:
                    values = await getValues(dateValues.value, 'allAdjustmentReasons', 'value/bytype/' + common.valueTypes.adjustmentReason.id, tenant);
                    result.adjustmentReasons = values;
                    break;
                case common.cacheValues.ledgerEntryType:
                    values = await getValues(dateValues.value, 'allLedgerEntryTypes', 'value/bytype/' + common.valueTypes.ledgerEntryType.id, tenant);
                    result.ledgerEntryTypes = values;
                    break;
                case common.cacheValues.processingType:
                    values = await getValues(dateValues.value, 'allProcessingType', 'value/bytype/' + common.valueTypes.processingType.id, tenant);
                    result.processingTypes = values;
                    break;
                case common.cacheValues.strainType:
                    values = await getValues(dateValues.value, 'allStrainType', 'value/bytype/' + common.valueTypes.strainType.id, tenant);
                    result.strainTypes = values;
                    break;
                case common.cacheValues.receiptType:
                    values = await getValues(dateValues.value, 'allReceiptType', 'value/bytype/' + common.valueTypes.receiptType.id, tenant);
                    result.receiptTypes = values;
                    break;
                case common.cacheValues.shipmentType:
                    values = await getValues(dateValues.value, 'allShipmentType', 'value/bytype/' + common.valueTypes.shipmentType.id, tenant);
                    result.shipmentTypes = values;
                    break;
                case common.cacheValues.scheduleStatus:
                    values = await getValues(dateValues.value, 'allScheduleStatus', 'value/bytype/' + common.valueTypes.scheduleStatus.id, tenant);
                    result.scheduleStatuses = values;
                    break;
                case common.cacheValues.binType:
                    values = await getValues(dateValues.value, 'binTypes', 'value/bytype/' + common.valueTypes.binType.id, tenant);
                    result.binTypes = values;
                    break;
                case common.cacheValues.cullReason:
                    values = await getValues(dateValues.value, 'allCullReasons', 'value/bytype/' + common.valueTypes.cullReason.id, tenant);
                    result.cullReasons = values;
                    break;
                case common.cacheValues.batchTemplateStatus:
                    values = await getValues(dateValues.value, 'allBatchTemplateStatus', 'value/bytype/' + common.valueTypes.batchTemplateStatus.id, tenant);
                    result.batchTemplateStatuses = values;
                    break;
                case common.cacheValues.batchStartType:
                    values = await getValues(dateValues.value, 'allBatchStartType', 'value/bytype/' + common.valueTypes.batchStartType.id, tenant);
                    result.batchStartTypes = values;
                    break;
                case common.cacheValues.batchType:
                    values = await getValues(dateValues.value, 'allBatchType', 'value/bytype/' + common.valueTypes.batchType.id, tenant);
                    result.batchTypes = values;
                    break;
                case common.cacheValues.plantType:
                    values = await getValues(dateValues.value, 'allPlantType', 'value/bytype/' + common.valueTypes.plantType.id, tenant);
                    result.plantTypes = values;
                    break;
                case common.cacheValues.regulator:
                    values = await getValues(dateValues.value, 'allRegulators', 'value/bytype/' + common.valueTypes.regulators.id, tenant);
                    result.regulators = values;
                    break;
                case common.cacheValues.priority:
                    values = await getValues(dateValues.value, 'allPriorities', 'value/bytype/' + common.valueTypes.priority.id, tenant);
                    result.priorities = values;
                    break;
                case common.cacheValues.carType:
                    values = await getValues(dateValues.value, 'allCarTypes', 'value/bytype/' + common.valueTypes.carType.id, tenant);
                    result.carTypes = values;
                    break;
                case common.cacheValues.impactAssessment:
                    values = await getValues(dateValues.value, 'allImpactAssessments', 'value/bytype/' + common.valueTypes.impactAssessment.id, tenant);
                    result.impactAssessments = Array.isArray(values) ? values : [];
                    break;
                case common.cacheValues.changeReason:
                    values = await getValues(dateValues.value, 'allChangeReasons', 'value/bytype/' + common.valueTypes.changeReason.id, tenant);
                    result.changeReasons = values;
                    break;
                case common.cacheValues.applicationMethod:
                    values = await getValues(dateValues.value, 'allApplicationMethods', 'value/bytype/' + common.valueTypes.applicationMethod.id, tenant);
                    result.applicationMethods = values;
                    break;
                case common.cacheValues.cropTreatmentType:
                    values = await getValues(dateValues.value, 'allCropTreatmentTypes', 'value/bytype/' + common.valueTypes.cropTreatmentType.id, tenant);
                    result.cropTreatmentTypes = values;
                    break;
                case common.cacheValues.cropTreatmentSubType:
                    values = await getValues(dateValues.value, 'allCropTreatmentSubTypes', 'value/bytype/' + common.valueTypes.cropTreatmentSubType.id, tenant);
                    result.cropTreatmentSubTypes = values;
                    break;
                case common.cacheValues.curingActivity:
                    values = await getValues(dateValues.value, 'allCuringActivities', 'value/bytype/' + common.valueTypes.curingActivity.id, tenant);
                    result.curingActivities = values;
                    break;
                case common.cacheValues.batchDestructionReason:
                    values = await getValues(dateValues.value, 'allBatchDestructionReasions', 'value/bytype/' + common.valueTypes.batchDestructionReason.id, tenant);
                    result.batchDestructionReasons = values;
                    break;
                case common.cacheValues.feedingType:
                    values = await getValues(dateValues.value, 'feedingTypes', 'value/bytype/' + common.valueTypes.feedingType.id, tenant);
                    result.feedingTypes = values;
                    break;
                case common.cacheValues.trimType:
                    values = await getValues(dateValues.value, 'trimTypes', 'value/bytype/' + common.valueTypes.trimType.id, tenant);
                    result.trimTypes = values;
                    break;
                case common.cacheValues.tempUOM:
                    values = await getValues(dateValues.value, 'tempUOMs', 'value/bytype/' + common.valueTypes.temperatureUom.id, tenant);
                    result.tempUOMs = values;
                    break;
                case common.cacheValues.dryType:
                    values = await getValues(dateValues.value, 'dryTypes', 'value/bytype/' + common.valueTypes.dryingType.id, tenant);
                    result.dryingTypes = values;
                    break;
                case common.cacheValues.routingType:
                    values = await getValues(dateValues.value, 'routingTypes', 'value/bytype/' + common.valueTypes.routingType.id, tenant);
                    result.routingTypes = values;
                    break;
                case common.cacheValues.itemTrackingType:
                    values = await getValues(dateValues.value, 'itemTrackingTypes', 'value/bytype/' + common.valueTypes.itemTrackingType.id, tenant);
                    result.itemTrackingTypes = values;
                    break;
                case common.cacheValues.taxItem:
                    values = await getValues(dateValues.value, 'taxItems', 'value/bytype/' + common.valueTypes.taxItem.id, tenant);
                    result.taxItems = values;
                    break;
                case common.cacheValues.lotRating:
                    values = await getValues(dateValues.value, 'lotRatings', 'value/bytype/' + common.valueTypes.lotRating.id, tenant, tenant);
                    result.lotRatings = values;
                    break;
                case common.cacheValues.phoneType:
                    values = await getValues(dateValues.value, 'phoneTypes', 'value/bytype/' + common.valueTypes.phoneNumberType.id, tenant, tenant);
                    result.phoneTypes = values;
                    break;
                case common.cacheValues.vendorKPI:
                    values = await getValues(dateValues.value, 'vendorKPIs', 'value/bytype/' + common.valueTypes.vendorKPI.id, tenant);
                    result.vendorKPIs = values;
                    break;
                case common.cacheValues.vendorType:
                    values = await getValues(dateValues.value, 'vendorTypes', 'value/bytype/' + common.valueTypes.vendorType.id, tenant, tenant);
                    result.vendorTypes = values;
                    break;
                case common.cacheValues.licenseType:
                    values = await getValues(dateValues.value, 'licenseTypes', 'value/bytype/' + common.valueTypes.licenseType.id, tenant, tenant);
                    result.licenseTypes = values;
                    break;
                case common.cacheValues.qualificationStatus:
                    values = await getValues(dateValues.value, 'qualificationStatuses', 'value/bytype/' + common.valueTypes.qualificationStatus.id, tenant);
                    result.qualificationStatuses = values;
                    break;
                case common.cacheValues.certificationType:
                    values = await getValues(dateValues.value, 'certificationTypes', 'value/bytype/' + common.valueTypes.certificationType.id, tenant);
                    result.certificationTypes = values;
                    break;
                case common.cacheValues.purchaseOrderStatus:
                    values = await getValues(dateValues.value, 'purchaseOrderStatuses', 'value/bytype/' + common.valueTypes.purchaseOrderStatus.id, tenant);
                    result.purchaseOrderStatuses = values;
                    break;
                case common.cacheValues.destructionMethod:
                    values = await getValues(dateValues.value, 'destructionMethods', 'value/bytype/' + common.valueTypes.destructionMethod.id, tenant);
                    result.destructionMethods = values;
                    break;
                case common.cacheValues.roomCleaningType:
                    values = await getValues(dateValues.value, 'roomCleaningTypes', 'value/bytype/' + common.valueTypes.roomCleaningType.id, tenant);
                    result.roomCleaningTypes = values;
                    break;
                case common.cacheValues.equipmentType:
                    values = await getValues(dateValues.value, 'equipmentTypes', 'value/bytype/' + common.valueTypes.equipmentType.id, tenant);
                    result.equipmentTypes = values;
                    break;
                case common.cacheValues.maintenanceRequestCategory:
                    values = await getValues(dateValues.value, 'maintenanceRequestCategories', 'value/bytype/' + common.valueTypes.maintRequestCategory.id, tenant);
                    result.maintenanceRequestCategories = values;
                    break;
                case common.cacheValues.addressType:
                    values = await getValues(dateValues.value, 'addressTypes', 'value/bytype/' + common.valueTypes.addressType.id, tenant);
                    result.addressTypes = values;
                    break;
                case common.cacheValues.relation:
                    values = await getValues(dateValues.value, 'relations', 'value/bytype/' + common.valueTypes.relation.id, tenant);
                    result.relations = values;
                    break;
                case common.cacheValues.gender:
                    values = await getValues(dateValues.value, 'genders', 'value/bytype/' + common.valueTypes.gender.id, tenant);
                    result.genders = values;
                    break;
                case common.cacheValues.customerKPI:
                    values = await getValues(dateValues.value, 'customerKPIs', 'value/bytype/' + common.valueTypes.customerKPI.id, tenant);
                    result.customerKPIs = values;
                    break;
                case common.cacheValues.customerType:
                    values = await getValues(dateValues.value, 'customerTypes', 'value/bytype/' + common.valueTypes.customerType.id, tenant);
                    result.customerTypes = values;
                    break;
                case common.cacheValues.saleType:
                    values = await getValues(dateValues.value, 'saleTypes', 'value/bytype/' + common.valueTypes.saleType.id, tenant);
                    result.saleTypes = values;
                    break;
                case common.cacheValues.announcementStatus:
                    values = await getValues(dateValues.value, 'announcementStatuses', 'value/bytype/' + common.valueTypes.announcementStatus.id);
                    result.announcementStatuses = values;
                    break;
                case common.cacheValues.announcementType:
                    values = await getValues(dateValues.value, 'announcementTypes', 'value/bytype/' + common.valueTypes.announcementType.id);
                    result.announcementTypes = values;
                    break;
                case common.cacheValues.announcementPriority:
                    values = await getValues(dateValues.value, 'announcementPriorities', 'value/bytype/' + common.valueTypes.announcementPriority.id);
                    result.announcementPriorities = values;
                    break;
                case common.cacheValues.lengthUOM:
                    values = await getValues(dateValues.value, 'lengthUOMs', 'value/bytype/' + common.valueTypes.lengthUom.id, tenant);
                    result.lengthUOMs = values;
                    break;
                case common.cacheValues.productionPlanStatus:
                    values = await getValues(dateValues.value, 'productionPlanStatuses', 'value/bytype/' + common.valueTypes.productionPlanStatus.id, tenant);
                    result.productionPlanStatuses = values;
                    break;
                case common.cacheValues.treatmentType:
                    values = await getValues(dateValues.value, 'treatmentTypes', 'value/bytype/' + common.valueTypes.treatmentType.id, tenant);
                    result.treatmentTypes = values;
                    break;
                case common.cacheValues.externalTreatmentIssue:
                    values = await getValues(dateValues.value, 'externalTreatmentIssues', 'value/bytype/' + common.valueTypes.externalTreatmentIssue.id, tenant);
                    result.externalTreatmentIssues = values;
                    break;
                case common.cacheValues.sterilizationType:
                    values = await getValues(dateValues.value, 'sterilizationTypes', 'value/bytype/' + common.valueTypes.sterilizationType.id, tenant);
                    result.sterilizationTypes = values;
                    break;
                case common.cacheValues.customerComplaintType:
                    values = await getValues(dateValues.value, 'customerComplaintTypes', 'value/bytype/' + common.valueTypes.customerComplaintType.id, tenant);
                    result.customerComplaintTypes = values;
                    break;
                case common.cacheValues.seriousReaction:
                    values = await getValues(dateValues.value, 'seriousReactions', 'value/bytype/' + common.valueTypes.seriousReaction.id, tenant);
                    result.seriousReactions = values;
                    break;
                case common.cacheValues.deviationType:
                    values = await getValues(dateValues.value, 'deviationTypes', 'value/bytype/' + common.valueTypes.deviationType.id, tenant);
                    result.deviationTypes = values;
                    break;
                case common.cacheValues.deviationImpact:
                    values = await getValues(dateValues.value, 'deviationImpacts', 'value/bytype/' + common.valueTypes.deviationImpact.id, tenant);
                    result.deviationImpacts = values;
                    break;
                case common.cacheValues.deviationCategory:
                    values = await getValues(dateValues.value, 'deviationCategories', 'value/bytype/' + common.valueTypes.deviationCategory.id, tenant);
                    result.deviationCategories = values;
                    break;
                case common.cacheValues.deviationStatus:
                    values = await getValues(dateValues.value, 'deviationStatuses', 'value/bytype/' + common.valueTypes.deviationStatus.id, tenant);
                    result.deviationStatuses = values;
                    break;
                case common.cacheValues.deviationDisposition:
                    values = await getValues(dateValues.value, 'deviationDispositions', 'value/bytype/' + common.valueTypes.deviationDisposition.id, tenant);
                    result.deviationDispositions = values;
                    break;
                case common.cacheValues.action:
                    values = await getValues(dateValues.value, 'actions', 'value/bytype/' + common.valueTypes.action.id, tenant);
                    result.actions = values;
                    break;
                case common.cacheValues.productionScheduleCancelReason:
                    values = await getValues(dateValues.value, 'productionScheduleCancelReasons', 'value/bytype/' + common.valueTypes.productionScheduleCancelReason.id, tenant);
                    result.productionScheduleCancelReasons = values;
                    break;
                case common.cacheValues.productionScheduleStatus:
                    values = await getValues(dateValues.value, 'productionScheduleStatuses', 'value/bytype/' + common.valueTypes.productionScheduleStatus.id, tenant);
                    result.productionScheduleStatuses = values;
                    break;
                case common.cacheValues.observationType:
                    values = await getValues(dateValues.value, 'observationTypes', 'value/bytype/' + common.valueTypes.observationType.id, tenant);
                    result.observationTypes = values;
                    break;
                case common.cacheValues.documentCategory:
                    values = await getValues(dateValues.value, 'documentCategories', 'value/bytype/' + common.valueTypes.documentCategory.id, tenant);
                    result.documentCategories = values;
                    break;
                case common.cacheValues.documentFormat:
                    values = await getValues(dateValues.value, 'documentFormats', 'value/bytype/' + common.valueTypes.documentFormat.id, tenant);
                    result.documentFormats = values;
                    break;
                case common.cacheValues.documentStatus:
                    values = await getValues(dateValues.value, 'documentStatuses', 'value/bytype/' + common.valueTypes.documentStatus.id, tenant);
                    result.documentStatuses = values;
                    break;
                case common.cacheValues.documentType:
                    values = await getValues(dateValues.value, 'documentTypes', 'value/bytype/' + common.valueTypes.documentType.id, tenant);
                    result.documentTypes = values;
                    break;
                case common.cacheValues.bomLineType:
                    values = await getValues(dateValues.value, 'bomLineTypes', 'value/bytype/' + common.valueTypes.bomLineType.id, tenant);
                    result.bomLineTypes = values;
                    break;
                case common.cacheValues.ccxPostingStatus:
                    values = await getValues(dateValues.value, 'ccxPostingStatuses', 'value/bytype/' + common.valueTypes.ccxPostingStatus.id, tenant);
                    result.ccxPostingStatuses = values;
                    break;
                case common.cacheValues.growRoomType:
                    values = await getValues(dateValues.value, 'growRoomTypes', 'value/bytype/' + common.valueTypes.growRoomType.id, tenant);
                    result.growRoomTypes = values;
                    break;
                case common.cacheValues.exciseAdjustmentType:
                    values = await getValues(dateValues.value, 'exciseAdjustmentTypes', 'value/bytype/' + common.valueTypes.exciseAdjustmentType.id, tenant);
                    result.exciseAdjustmentTypes = values;
                    break;
                case common.cacheValues.maintenanceLogType:
                    values = await getValues(dateValues.value, 'maintenanceLogTypes', 'value/bytype/' + common.valueTypes.maintenanceLogType.id, tenant);
                    result.maintenanceLogTypes = values;
                    break;
                case common.cacheValues.equipmentCalibrationCheckType:
                    values = await getValues(dateValues.value, 'equipmentCalibrationCheckTypes', 'value/bytype/' + common.valueTypes.equipmentCalibrationCheckType.id, tenant);
                    result.equipmentCalibrationCheckTypes = values;
                    break;
                case common.cacheValues.machineType:
                    values = await getValues(dateValues.value, 'machineTypes', 'value/bytype/' + common.valueTypes.machineType.id, tenant);
                    result.machineTypes = values;
                    break;
                case common.cacheValues.saleTerms:
                    values = await getValues(dateValues.value, 'saleTerms', 'value/bytype/' + common.valueTypes.saleTerms.id, tenant);
                    result.saleTerms = values;
                    break;
                case common.cacheValues.testResultValue:
                    values = await getValues(dateValues.value, 'testResultValues', 'value/bytype/' + common.valueTypes.testResultValue.id, tenant);
                    result.testResultValues = values;
                    break;
                case common.cacheValues.testTotalType:
                    values = await getValues(dateValues.value, 'testTypeTotals', 'value/bytype/' + common.valueTypes.testTotalType.id, tenant);
                    result.testTotalTypes = values;
                    break;
                case common.cacheValues.carSourceType:
                    values = await getValues(dateValues.value, 'carSourceTypes', 'value/bytype/' + common.valueTypes.carSourceType.id, tenant);
                    result.carSourceTypes = values;
                    break;
                case common.cacheValues.documentCertificationStatus:
                    values = await getValues(dateValues.value, 'documentCertificationStatuses', 'value/bytype/' + common.valueTypes.documentCertificationStatus.id, tenant);
                    result.documentCertificationStatuses = values;
                    break;
                case common.cacheValues.feedingFrequency:
                    values = await getValues(dateValues.value, 'feedingFrequencies', 'value/bytype/' + common.valueTypes.feedingFrequency.id, tenant);
                    result.feedingFrequencies = values;
                    break;
                case common.cacheValues.otherCost:
                    values = await getValues(dateValues.value, 'otherCosts', 'value/bytype/' + common.valueTypes.otherCost.id, tenant);
                    result.otherCosts = values;
                    break;
                case common.cacheValues.vendorCategory:
                    values = await getValues(dateValues.value, 'vendorCategories', 'value/bytype/' + common.valueTypes.vendorCategory.id, tenant);
                    result.vendorCategories = values;
                    break;
                case common.cacheValues.auditCategory:
                    values = await getValues(dateValues.value, 'auditCategories', 'value/bytype/' + common.valueTypes.auditCategory.id, tenant);
                    result.auditCategories = values;
                    break;
                case common.cacheValues.auditRating:
                    values = await getValues(dateValues.value, 'auditRatings', 'value/bytype/' + common.valueTypes.auditRating.id, tenant);
                    result.auditRatings = values;
                    break;
                case common.cacheValues.auditStandard:
                    values = await getValues(dateValues.value, 'auditStandards', 'value/bytype/' + common.valueTypes.auditStandard.id, tenant);
                    result.auditStandards = values;
                    break;
                case common.cacheValues.auditType:
                    values = await getValues(dateValues.value, 'auditTypes', 'value/bytype/' + common.valueTypes.auditType.id, tenant);
                    result.auditTypes = values;
                    break;
                case common.cacheValues.lotType:
                    values = await getValues(dateValues.value, 'lotTypes', 'value/bytype/' + common.valueTypes.lotType.id, tenant);
                    result.lotTypes = values;
                    break;
                case common.cacheValues.sopRelatedTo:
                    values = await getValues(dateValues.value, 'sopRelatedTos', 'value/bytype/' + common.valueTypes.sopRelatedTo.id, tenant);
                    result.sopRelatedTos = values;
                    break;
                case common.cacheValues.valueType:
                    values = await getValues(dateValues.value, 'allValueTypes', 'valuetype/select', tenant);
                    result.valueTypes = values;
                    break;
                case common.cacheValues.userDepartment:
                    values = await getValues(dateValues.department, 'userDepartments', 'userdepartments', tenant);
                    result.userDepartments = values;
                    break;
                case common.cacheValues.productionItem:
                    values = await getValues(dateValues.item, 'allProductionItems', 'item/production', tenant);
                    result.productionItems = values;
                    break;
                case common.cacheValues.producedItem:
                    values = await getValues(dateValues.item, 'allProducedItems', 'item/produced', tenant);
                    result.producedItems = values;
                    break;
                case common.cacheValues.exciseItems:
                    values = await getValues(dateValues.item, 'allExciseItems', 'item/excise', tenant);
                    result.exciseItems = values;
                    break;
                case common.cacheValues.ppeItem:
                    values = await getValues(dateValues.item, 'allPpeItems', 'item/ppe', tenant);
                    result.ppeItems = values;
                    break;
                case common.cacheValues.pestTrapItem:
                    values = await getValues(dateValues.item, 'allPestTrapItems', 'item/pesttrap', tenant);
                    result.pestTrapItems = values;
                    break;
                case common.cacheValues.feedingRecipeItem:
                    values = await getValues(dateValues.item, 'feedingRecipeItems', 'item/feedrecipe', tenant);
                    result.feedingRecipeItems = values;
                    break;
                case common.cacheValues.feedingLotItem:
                    values = await getValues(dateValues.item, 'allFeedLotItems', 'item/feedinglot', tenant);
                    result.feedingLotItems = values;
                    break;
                case common.cacheValues.feedingSolutionItem:
                    values = await getValues(dateValues.item, 'allFeedSolutionItems', 'item/feedingsolution', tenant);
                    result.feedingSolutionItems = values;
                    break;
                case common.cacheValues.feedingLotIngredient:
                    values = await getValues(dateValues.item, 'allFeedIngredients', 'item/feedlotingredients', tenant);
                    result.feedingLotIngredients = values;
                    break;
                case common.cacheValues.roomFeedingItem:
                    values = await getValues(dateValues.item, 'allRoomFeedIngredients', 'item/feedingredients', tenant);
                    result.roomFeedingIngredients = values;
                    break;
                case common.cacheValues.pesticidePrepItems:
                    values = await getValues(dateValues.item, 'pesticidePrepItems', 'item/pesticidesolutions', tenant);
                    result.pesticideSolutions = values;
                    break;
                case common.cacheValues.pesticidePrepInputs:
                    values = await getValues(dateValues.item, 'pesticidePrepInputs', 'item/pesticideinputs', tenant);
                    result.pesticideInputs = values;
                    break;
                case common.cacheValues.trimmingStartItem:
                    values = await getValues(dateValues.item, 'trimmingStartItems', 'item/trimstart', tenant);
                    result.trimStartItems = values;
                    break;
                case common.cacheValues.cureStartItem:
                    values = await getValues(dateValues.item, 'cureStartItems', 'item/curestart', tenant);
                    result.cureStartItems = values;
                    break;
                case common.cacheValues.dryStartItem:
                    values = await getValues(dateValues.item, 'dryStartItems', 'item/drystart', tenant);
                    result.dryStartItems = values;
                    break;
                case common.cacheValues.wasteItems:
                    values = await getValues(dateValues.item, 'wasteItems', 'item/waste', tenant);
                    result.wasteItems = values;
                    break;
                case common.cacheValues.cleaningSolutionItem:
                    values = await getValues(dateValues.item, 'cleaningSolutionItems', 'item/cleaningsolution', tenant);
                    result.cleaningSolutionItems = values;
                    break;
                case common.cacheValues.cleanerItem:
                    values = await getValues(dateValues.item, 'cleanerItems', 'item/cleaner', tenant);
                    result.cleanerItems = values;
                    break;
                case common.cacheValues.batchActivityItems:
                    values = await getValues(dateValues.item, 'batchActivityItems', 'item/batchact', tenant);
                    result.batchActivityItems = values;
                    break;
                case common.cacheValues.ccxFlowerItem:
                    values = await getValues(dateValues.item, 'ccxFlowerItems', 'item/ccxflower', tenant);
                    result.ccxFlowerItems = values;
                    break;
                case common.cacheValues.ccxTrimItem:
                    values = await getValues(dateValues.item, 'ccxTrimItems', 'item/ccxtrim', tenant);
                    result.ccxTrimItems = values;
                    break;
                case common.cacheValues.casedItem:
                    values = await getValues(dateValues.item, 'caseditems', 'item/cased', tenant);
                    result.casedItems = values;
                    break;
                case common.cacheValues.packagedItem:
                    values = await getValues(dateValues.item, 'packagedItems', 'item/packaged', tenant);
                    result.packagedItems = values;
                    break;
                case common.cacheValues.caseLotItems:
                    values = await getValues(dateValues.item, 'caseLotItems', 'item/caselot', tenant);
                    result.caseLotItems = values;
                    break;
                case common.cacheValues.testableItem:
                    values = await getValues(dateValues.item, 'testableItems', 'item/testable', tenant);
                    result.testableItems = values;
                    break;
                case common.cacheValues.team:
                    values = await getValues(dateValues.item, 'teams', 'team/select', tenant);
                    result.teams = values;
                    break;
                case common.cacheValues.batchTemplate:
                    values = await getValues(dateValues.batchTemplate, 'allBatchTemplates', 'batchtemplate/select', tenant);
                    result.batchTemplates = values;
                    break;
                case common.cacheValues.batchTemplateActive:
                    values = await getValues(dateValues.batchTemplate, 'activeBatchTemplates', 'batchtemplate/select?active=true', tenant);
                    result.activeBatchTemplates = values;
                    break;
                case common.cacheValues.batchActivityDef:
                    values = await getValues(dateValues.batchActivityDef, 'allBatchAcitvityDefs', 'batchactivitydefinition/select', tenant);
                    result.batchActivityDefs = values;
                    break;
                case common.cacheValues.batchActivityDefRegular:
                    values = await getValues(dateValues.batchActivityDef, 'batchAcitvityDefsReg', 'batchactivitydefinition/selectregular', tenant);
                    result.batchActivityDefsReg = values;
                    break;
                case common.cacheValues.batchActivityDefTissue:
                    values = await getValues(dateValues.batchActivityDef, 'batchAcitvityDefsTissue', 'batchactivitydefinition/selecttissue', tenant);
                    result.batchActivityDefsTissue = values;
                    break;
                case common.cacheValues.managedDocument:
                    values = await getValues(dateValues.managedDocument, 'allManagedDocuments', 'manageddoc/select', tenant);
                    result.managedDocuments = values;
                    break;
                case common.cacheValues.documentAuthor:
                    values = await getValues(dateValues.user, 'allDocumentAuthors', 'documentauthors', tenant);
                    result.documentAuthors = values;
                    break;
                case common.cacheValues.documentReviewer:
                    values = await getValues(dateValues.user, 'documentReviewers', 'documentreviewers', tenant);
                    result.documentReviewers = values;
                    break;
                case common.cacheValues.feedingRecipe:
                    values = await getValues(dateValues.feedingRecipe, 'activeFeedingRecipes', 'feedingrecipe/select', tenant);
                    result.feedingRecipes = values;
                    break;
                case common.cacheValues.feedingRecipeAll:
                    values = await getValues(dateValues.feedingRecipe, 'allFeedingRecipes', 'feedingrecipe/select?hide=true', tenant);
                    result.feedingRecipesAll = values;
                    break;
                case common.cacheValues.productionSchedule:
                    values = await getValues(dateValues.productionSchedules, 'prodScheds', 'productionschedule/select', tenant);
                    result.productionSchedules = values;
                    break;
                case common.cacheValues.userSop:
                    values = await getValues(dateValues.sop, 'userSops', 'usersops', tenant);
                    result.userSops = values;
                    break;
                case common.cacheValues.managedDocumentTemplate:
                    values = await getValues(dateValues.managedDocumentTemplate, 'managedDocumentTemplateVals', 'documenttemplate/select', tenant);
                    result.managedDocumentTemplates = values;
                    break;
                case common.cacheValues.tenantReport:
                    values = await getValues(dateValues.tenantReport, 'tenantReportVals', 'tenantreportselect/' + objectId, tenant);
                    result.tenantReports = values;
                    break;
                case common.cacheValues.cleaningSolutionRecipe:
                    values = await getValues(dateValues.cleaningSolutionRecipe, 'cleaningSolutionRecipeVals', 'cleaningrecipe/select', tenant);
                    result.cleaningSolutionRecipes = values;
                    break;
                case common.cacheValues.gtin:
                    values = await getValues(dateValues.item, 'gtinSelect', 'gtin/select', tenant);
                    result.gtins = values;
                    break;
                case common.cacheValues.qbItem:
                    values = await getValues(dateValues.item, 'qbItems', 'quickbooks/itemselect', tenant);
                    result.qbItems = values;
                    break;
                case common.cacheValues.qbCustomer:
                    values = await getValues(dateValues.item, 'qbCustomers', 'quickbooks/customerselect', tenant);
                    result.qbCustomers = values;
                    break;
                case common.cacheValues.qbVendor:
                    values = await getValues(dateValues.item, 'qbVendors', 'quickbooks/vendorselect', tenant);
                    result.qbVendors = values;
                    break;
                case common.cacheValues.childTenant:
                    values = await getValues(dateValues.item, 'childTenants', 'tenant/children', tenant);
                    result.childTenants = values;
                    break;
                case common.cacheValues.relatedTenant:
                    values = await getValues(dateValues.item, 'relatedTenants', 'tenant/related', tenant);
                    result.relatedTenants = values;
                    break;
                case common.cacheValues.location:
                case common.cacheValues.cultivationLocation:
                case common.cacheValues.processingLocation:
                case common.cacheValues.warehouseLocation:
                case common.cacheValues.workLocation:
                case common.cacheValues.activeLocation:
                case common.cacheValues.cultivationWarehouseLocation:
                case common.cacheValues.processingWarehouseLocation:
                    values = await getLocations(dateValues.location, row, tenant);
                    result.locations = values;
                    break;
                case common.cacheValues.room:
                case common.cacheValues.growingRoom:
                case common.cacheValues.processingRoom:
                case common.cacheValues.storageRoom:
                case common.cacheValues.defaultRoom:
                case common.cacheValues.defaultGrowingRoom:
                case common.cacheValues.defaultProcessingRoom:
                case common.cacheValues.defaultStorageRoom:
                    values = await getRooms(dateValues.room, row, tenant);
                    result.rooms = values;
                    break;
                case common.cacheValues.batchActivitySop:
                    values = await getValues(dateValues.sop, 'batchActivitySops', 'objectSops/' + constants.objectIds.batchActivity, tenant);
                    result.sops = values;
                    break;
                case common.cacheValues.batchActivitySopAll:
                    values = await getValues(dateValues.sop, 'allBatchActivitySops', 'objectSops/' + constants.objectIds.batchActivity + '?all=true', tenant);
                    result.allSops = values;
                    break;
                case common.cacheValues.cleaningSolutionSop:
                    values = await getValues(dateValues.sop, 'cleaningSolutionSops', 'objectSops/' + constants.objectIds.cleaningSolution, tenant);
                    result.sops = values;
                    break;
                case common.cacheValues.cleaningSolutionSopAll:
                    values = await getValues(dateValues.sop, 'allCleaningSolutionSops', 'objectSops/' + constants.objectIds.cleaningSolution + '?all=true', tenant);
                    result.allSops = values;
                    break;
                case common.cacheValues.cropTreatmentSop:
                    values = await getValues(dateValues.sop, 'cropTreatmentSops', 'objectSops/' + constants.objectIds.cropTreatment, tenant);
                    result.sops = values;
                    break;
                case common.cacheValues.cropTreatmentSopAll:
                    values = await getValues(dateValues.sop, 'allCropTreatmentSops', 'objectSops/' + constants.objectIds.cropTreatment + '?all=true', tenant);
                    result.allSops = values;
                    break;
                case common.cacheValues.curingActivitySop:
                    values = await getValues(dateValues.sop, 'curingActivitySops', 'objectSops/' + constants.objectIds.curingActivity, tenant);
                    result.sops = values;
                    break;
                case common.cacheValues.curingActivitySopAll:
                    values = await getValues(dateValues.sop, 'allCuringActivitySops', 'objectSops/' + constants.objectIds.curingActivity + '?all=true', tenant);
                    result.allSops = values;
                    break;
                case common.cacheValues.feedingLotSop:
                    values = await getValues(dateValues.sop, 'feedingLotSops', 'objectSops/' + constants.objectIds.feedingLot, tenant);
                    result.sops = values;
                    break;
                case common.cacheValues.feedingLotSopAll:
                    values = await getValues(dateValues.sop, 'allFeedingLotSops', 'objectSops/' + constants.objectIds.feedingLot + '?all=true', tenant);
                    result.allSops = values;
                    break;
                case common.cacheValues.roomFeedingSop:
                    values = await getValues(dateValues.sop, 'roomFeedingSop', 'objectSops/' + constants.objectIds.roomFeeding, tenant);
                    result.sops = values;
                    break;
                case common.cacheValues.roomFeedingSopAll:
                    values = await getValues(dateValues.sop, 'roomFeedingSopAll', 'objectSops/' + constants.objectIds.roomFeeding + '?all=true', tenant);
                    result.allSops = values;
                    break;
                case common.cacheValues.pesticidePrepSop:
                    values = await getValues(dateValues.sop, 'pesticidePrepSop', 'objectSops/' + constants.objectIds.pesticidePrep, tenant);
                    result.sops = values;
                    break;
                case common.cacheValues.pesticidePrepSopAll:
                    values = await getValues(dateValues.sop, 'pesticidePrepSopAll', 'objectSops/' + constants.objectIds.pesticidePrep + '?all=true', tenant);
                    result.allSops = values;
                    break;
                case common.cacheValues.trimmingActivitySop:
                    values = await getValues(dateValues.sop, 'trimmingActivitySops', 'objectSops/' + constants.objectIds.trimmingActivity, tenant);
                    result.sops = values;
                    break;
                case common.cacheValues.trimmingActivitySopAll:
                    values = await getValues(dateValues.sop, 'trimmingActivitySopAll', 'objectSops/' + constants.objectIds.trimmingActivity + '?all=true', tenant);
                    result.allSops = values;
                    break;
                case common.cacheValues.dryingActivitySop:
                    values = await getValues(dateValues.sop, 'dryingActivitySops', 'objectSops/' + constants.objectIds.dryingActivity, tenant);
                    result.sops = values;
                    break;
                case common.cacheValues.dryingActivitySopAll:
                    values = await getValues(dateValues.sop, 'dryingActivitySopAll', 'objectSops/' + constants.objectIds.dryingActivity + '?all=true', tenant);
                    result.allSops = values;
                    break;
                case common.cacheValues.productionOrderSop:
                    values = await getValues(dateValues.sop, 'productionOrderSop', 'objectSops/' + constants.objectIds.productionOrder, tenant);
                    result.sops = values;
                    break;
                case common.cacheValues.productionOrderSopAll:
                    values = await getValues(dateValues.sop, 'productionOrderSopAll', 'objectSops/' + constants.objectIds.productionOrder + '?all=true', tenant);
                    result.allSops = values;
                    break;
                case common.cacheValues.productionOrderActivitySop:
                    values = await getValues(dateValues.sop, 'productionOrderActivitySop', 'objectSops/' + constants.objectIds.poActivity, tenant);
                    result.sops = values;
                    break;
                case common.cacheValues.productionOrderActivitySopAll:
                    values = await getValues(dateValues.sop, 'productionOrderActivitySopAll', 'objectSops/' + constants.objectIds.poActivity + '?all=true', tenant);
                    result.allSops = values;
                    break;
                case common.cacheValues.shipmentSop:
                    values = await getValues(dateValues.sop, 'shipmentSop', 'objectSops/' + constants.objectIds.shipment, tenant);
                    result.sops = values;
                    break;
                case common.cacheValues.shipmentSopAll:
                    values = await getValues(dateValues.sop, 'shipmentSopAll', 'objectSops/' + constants.objectIds.shipment + '?all=true', tenant);
                    result.allSops = values;
                    break;
                case common.cacheValues.receiptSop:
                    values = await getValues(dateValues.sop, 'receiptSop', 'objectSops/' + constants.objectIds.receipt, tenant);
                    result.sops = values;
                    break;
                case common.cacheValues.receiptSopAll:
                    values = await getValues(dateValues.sop, 'receiptSopAll', 'objectSops/' + constants.objectIds.receipt + '?all=true', tenant);
                    result.allSops = values;
                    break;
                case common.cacheValues.destructionSop:
                    values = await getValues(dateValues.sop, 'destructionSop', 'objectSops/' + constants.objectIds.destruction, tenant);
                    result.sops = values;
                    break;
                case common.cacheValues.destructionSopAll:
                    values = await getValues(dateValues.sop, 'destructionSopAll', 'objectSops/' + constants.objectIds.destruction + '?all=true', tenant);
                    result.allSops = values;
                    break;
                case common.cacheValues.roomCleaningSop:
                    values = await getValues(dateValues.sop, 'roomCleaningSops', 'objectSops/' + constants.objectIds.roomCleaning, tenant);
                    result.sops = values;
                    break;
                case common.cacheValues.roomCleaningSopAll:
                    values = await getValues(dateValues.sop, 'roomCleaningSopAll', 'objectSops/' + constants.objectIds.roomCleaning + '?all=true', tenant);
                    result.allSops = values;
                    break;
                case common.cacheValues.locationAuditSop:
                    values = await getValues(dateValues.sop, 'locationAuditSop', 'objectSops/' + constants.objectIds.locationAudit, tenant);
                    result.sops = values;
                    break;
                case common.cacheValues.locationAuditSopAll:
                    values = await getValues(dateValues.sop, 'locationAuditSopAll', 'objectSops/' + constants.objectIds.locationAudit + '?all=true', tenant);
                    result.allSops = values;
                    break;
                case common.cacheValues.roomCheckSop:
                    values = await getValues(dateValues.sop, 'roomCheckSops', 'objectSops/' + constants.objectIds.roomCheck, tenant);
                    result.sops = values;
                    break;
                case common.cacheValues.roomCheckSopAll:
                    values = await getValues(dateValues.sop, 'roomCheckSopAll', 'objectSops/' + constants.objectIds.roomCheck + '?all=true', tenant);
                    result.allSops = values;
                    break;
                case common.cacheValues.equipmentCalibrationSop:
                    values = await getValues(dateValues.sop, 'equipmentCalibrationSops', 'objectSops/' + constants.objectIds.equipmentCalibrationCheck, tenant);
                    result.sops = values;
                    break;
                case common.cacheValues.equipmentCalibrationSopAll:
                    values = await getValues(dateValues.sop, 'equipmentCalibrationSopAll', 'objectSops/' + constants.objectIds.equipmentCalibrationCheck + '?all=true', tenant);
                    result.allSops = values;
                    break;
                case common.cacheValues.customerComplaintSop:
                    values = await getValues(dateValues.sop, 'customerComplaintSops', 'objectSops/' + constants.objectIds.customerComplaint, tenant);
                    result.sops = values;
                    break;
                case common.cacheValues.customerComplaintSopAll:
                    values = await getValues(dateValues.sop, 'customerComplaintSopAll', 'objectSops/' + constants.objectIds.customerComplaint + '?all=true', tenant);
                    result.allSops = values;
                    break;
                case common.cacheValues.cropTreatmentForm:
                    values = await getValues(dateValues.object, 'cropTreatmentForm', 'object/form/' + constants.objectIds.cropTreatment, tenant);
                    result.form = values;
                    break;
                case common.cacheValues.batchActivityForm:
                    values = await getValues(dateValues.object, 'batchActivityForm', 'object/form/' + constants.objectIds.batchActivity, tenant);
                    result.form = values;
                    break;
                case common.cacheValues.feedingLotForm:
                    values = await getValues(dateValues.object, 'feedingLotForm', 'object/form/' + constants.objectIds.feedingLot, tenant);
                    result.form = values;
                    break;
                case common.cacheValues.roomFeedingForm:
                    values = await getValues(dateValues.object, 'roomFeedingForm', 'object/form/' + constants.objectIds.roomFeeding, tenant);
                    result.form = values;
                    break;
                case common.cacheValues.pesticidPrepForm:
                    values = await getValues(dateValues.object, 'pesticidePrepForm', 'object/form/' + constants.objectIds.pesticidePrep, tenant);
                    result.form = values;
                    break;
                case common.cacheValues.trimmingOrderForm:
                    values = await getValues(dateValues.object, 'trimmingOrderForm', 'object/form/' + constants.objectIds.trimmingOrder, tenant);
                    result.form = values;
                    break;
                case common.cacheValues.trimmingActivityForm:
                    values = await getValues(dateValues.object, 'trimmingActivityForm', 'object/form/' + constants.objectIds.trimmingActivity, tenant);
                    result.form = values;
                    break;
                case common.cacheValues.dryingOrderForm:
                    values = await getValues(dateValues.object, 'dryingOrderForm', 'object/form/' + constants.objectIds.dryingOrder, tenant);
                    result.form = values;
                    break;
                case common.cacheValues.dryingActivityForm:
                    values = await getValues(dateValues.object, 'dryingActivityForm', 'object/form/' + constants.objectIds.dryingActivity, tenant);
                    result.form = values;
                    break;
                case common.cacheValues.curingForm:
                    values = await getValues(dateValues.object, 'curingForm', 'object/form/' + constants.objectIds.curing, tenant);
                    result.form = values;
                    break;
                case common.cacheValues.curingActivityForm:
                    values = await getValues(dateValues.object, 'curingActivityForm', 'object/form/' + constants.objectIds.curingActivity, tenant);
                    result.form = values;
                    break;
                case common.cacheValues.productionBatchForm:
                    values = await getValues(dateValues.object, 'productionBatchForm', 'object/form/' + constants.objectIds.productionBatch, tenant);
                    result.form = values;
                    break;
                case common.cacheValues.productionOrderForm:
                    values = await getValues(dateValues.object, 'productionOrderForm', 'object/form/' + constants.objectIds.productionOrder, tenant);
                    result.form = values;
                    break;
                case common.cacheValues.masterCaseForm:
                    values = await getValues(dateValues.object, 'masterCaseForm', 'object/form/' + constants.objectIds.masterCase, tenant);
                    result.form = values;
                    break;
                case common.cacheValues.productionOrderActivityForm:
                    values = await getValues(dateValues.object, 'productionOrderActivityForm', 'object/form/' + constants.objectIds.poActivity, tenant);
                    result.form = values;
                    break;
                case common.cacheValues.vendorForm:
                    values = await getValues(dateValues.object, 'vendorForm', 'object/form/' + constants.objectIds.vendor, tenant);
                    result.form = values;
                    break;
                case common.cacheValues.shipmentForm:
                    values = await getValues(dateValues.object, 'shipmentForm', 'object/form/' + constants.objectIds.shipment, tenant);
                    result.form = values;
                    break;
                case common.cacheValues.receiptForm:
                    values = await getValues(dateValues.object, 'receiptForm', 'object/form/' + constants.objectIds.receipt, tenant);
                    result.form = values;
                    break;
                case common.cacheValues.purchaseOrderForm:
                    values = await getValues(dateValues.object, 'purchaseOrderForm', 'object/form/' + constants.objectIds.purchaseOrder, tenant);
                    result.form = values;
                    break;
                case common.cacheValues.destructionForm:
                    values = await getValues(dateValues.object, 'destructionForm', 'object/form/' + constants.objectIds.destruction, tenant);
                    result.form = values;
                    break;
                case common.cacheValues.roomCleaningForm:
                    values = await getValues(dateValues.object, 'roomCleaningForm', 'object/form/' + constants.objectIds.roomCleaning, tenant);
                    result.form = values;
                    break;
                case common.cacheValues.equipmentLogForm:
                    values = await getValues(dateValues.object, 'equipmentLogForm', 'object/form/' + constants.objectIds.equipmentLog, tenant);
                    result.form = values;
                    break;
                case common.cacheValues.maintenanceRequestForm:
                    values = await getValues(dateValues.object, 'maintenanceRequestForm', 'object/form/' + constants.objectIds.maintenanceRequest, tenant);
                    result.form = values;
                    break;
                case common.cacheValues.cleaningSolutionForm:
                    values = await getValues(dateValues.object, 'cleaningSolutionForm', 'object/form/' + constants.objectIds.cleaningSolution, tenant);
                    result.form = values;
                    break;
                case common.cacheValues.customerForm:
                    values = await getValues(dateValues.object, 'customerForm', 'object/form/' + constants.objectIds.customer, tenant);
                    result.form = values;
                    break;
                case common.cacheValues.saleForm:
                    values = await getValues(dateValues.object, 'saleForm', 'object/form/' + constants.objectIds.sale, tenant);
                    result.form = values;
                    break;
                case common.cacheValues.externalTreatmentForm:
                    values = await getValues(dateValues.object, 'externalTreatmentForm', 'object/form/' + constants.objectIds.externalTreatment, tenant);
                    result.form = values;
                    break;
                case common.cacheValues.customerComplaintForm:
                    values = await getValues(dateValues.object, 'customerComplaintForm', 'object/form/' + constants.objectIds.customerComplaint, tenant);
                    result.form = values;
                    break;
                case common.cacheValues.deviationForm:
                    values = await getValues(dateValues.object, 'deviationForm', 'object/form/' + constants.objectIds.deviation, tenant);
                    result.form = values;
                    break;
                case common.cacheValues.batchObservationForm:
                    values = await getValues(dateValues.object, 'batchObservationForm', 'object/form/' + constants.objectIds.batchObservation, tenant);
                    result.form = values;
                    break;
                case common.cacheValues.equipmentCalibrationForm:
                    values = await getValues(dateValues.object, 'equipmentCalibrationForm', 'object/form/' + constants.objectIds.equipmentCalibrationCheck, tenant);
                    result.form = values;
                    break;
                case common.cacheValues.riskAssessmentForm:
                    values = await getValues(dateValues.object, 'riskAssessmentForm', 'object/form/' + constants.objectIds.riskAssessment, tenant);
                    result.form = values;
                    break;
                case common.cacheValues.testCategory:
                    values = await getValues(dateValues.object, 'testCategory', 'testcategories', tenant);
                    result.testCategories = values;
                    break;
                case common.cacheValues.labelPrintType:
                    values = await getValues(dateValues.value, 'allLabelTypes', 'value/bytype/' + common.valueTypes.labelPrintType.id, tenant);
                    result.labelPrintTypes = values;
                    break;
                case common.cacheValues.labelPrintQuality:
                    values = await getValues(dateValues.value, 'allLabelPrintQualities', 'value/bytype/' + common.valueTypes.labelPrintQuality.id, tenant);
                    result.labelPrintQualities = values;
                    break;
                case common.cacheValues.containerTypeAvailable:
                    values = await getValues(dateValues.value, 'containerTypeAvailables', 'containertypeavailable', tenant);
                    result.availableContainerTypes = values;
                    break;
                case common.cacheValues.containerTypeUnavailable:
                    values = await getValues(dateValues.value, 'containerTypeUnavailables', 'containertypeunavailable', tenant);
                    result.unavailableContainerTypes = values;
                    break;
                case common.cacheValues.containerTypeConsumption:
                    values = await getValues(dateValues.value, 'containerTypeConsumptions', 'containertypeconsumption', tenant);
                    result.consumptionContainerTypes = values;
                    break;
                case common.cacheValues.containerTypeLab:
                    values = await getValues(dateValues.value, 'containerTypeLabs', 'containertypelab', tenant);
                    result.labContainerTypes = values;
                    break;
                case common.cacheValues.containerTypeRetain:
                    values = await getValues(dateValues.value, 'containerTypeRetains', 'containertyperetain', tenant);
                    result.retainContainerTypes = values;
                    break;
                case common.cacheValues.containerTypeShipment:
                    values = await getValues(dateValues.value, 'containerTypeRetains', 'containertyperetain', tenant);
                    result.shipmentContainerTypes = values;
                    break;
                case common.cacheValues.currency:
                    values = await getCurrencyValues();
                    result.currencies = values;
                    break;
                case common.cacheValues.customerActivityType:
                    values = await getValues(dateValues.value, 'customerActivityTypes', 'value/bytype/' + common.valueTypes.customerActivityType.id, tenant);
                    result.customerActivityTypes = values;
                    break;
                case common.cacheValues.customerTaskType:
                    values = await getValues(dateValues.value, 'customerTaskTypes', 'value/bytype/' + common.valueTypes.customerTaskType.id, tenant);
                    result.customerTaskTypes = values;
                    break;
                case common.cacheValues.customerTaskPriority:
                    values = await getValues(dateValues.value, 'customerTaskPriorities', 'value/bytype/' + common.valueTypes.customerTaskPriority.id, tenant);
                    result.customerTaskPriorities = values;
                    break;
                case common.cacheValues.schedulePriority:
                    values = await getValues(dateValues.value, 'schedulePriorities', 'value/bytype/' + common.valueTypes.schedulePriority.id, tenant);
                    result.schedulePriorities = values;
                    break;
                case common.cacheValues.scheduleFrequency:
                    values = await getValues(dateValues.value, 'scheduleFrequencies', 'value/bytype/' + common.valueTypes.scheduleFrequency.id, tenant);
                    result.scheduleFrequencies = values;
                    break;
                case common.cacheValues.reminderTiming:
                    values = await getValues(dateValues.value, 'reminderTimings', 'value/bytype/' + common.valueTypes.reminderTiming.id, tenant);
                    result.reminderTimings = values;
                    break;
                default:
                    break;
            }
        }
        return result;
    },

    async getAdminValues(list, tenant) {
        let result = {};
        for (let row of list) {
            let values = null;
            switch (row) {
                case common.cacheValues.action:
                    values = await _getAdminValues('value/bytype/' + common.valueTypes.action.id);
                    result.actions = values;
                    break;
                case common.cacheValues.object:
                    values = await _getAdminValues('object/select', tenant);
                    result.objects = values;
                    break;
                case common.cacheValues.announcementPriority:
                    values = await _getAdminValues('value/bytype/' + common.valueTypes.announcementPriority.id);
                    result.announcementPriorities = values;
                    break;
                case common.cacheValues.announcementStatus:
                    values = await _getAdminValues('value/bytype/' + common.valueTypes.announcementStatus.id);
                    result.announcementStatuses = values;
                    break;
                case common.cacheValues.announcementType:
                    values = await _getAdminValues('value/bytype/' + common.valueTypes.announcementType.id);
                    result.announcementTypes = values;
                    break;
                case common.cacheValues.department:
                    values = await _getAdminValues('value/bytype/' + common.valueTypes.department.id, tenant);
                    result.departments = values;
                    break;
                case common.cacheValues.item:
                    values = await _getAdminValues('item/availableitems', tenant);
                    result.items = values;
                    break;
                case common.cacheValues.status:
                    values = await _getAdminValues('value/bytype/' + common.valueTypes.status.id, tenant);
                    result.statuses = values;
                    break;
                case common.cacheValues.user:
                    values = await _getAdminValues('user/all', tenant);
                    result.users = values;
                    break;
                default:
                    break;
            }
        }
        return result;
    },

    async screenUsers(screen, tenant) {
        let dateRes = await axios.get('/api/tenantupdateddates');
        let dateValues = dateRes.data;
        let data = null;
        if (screen != null && screen !== '') {
            const key = 'screen-' + tenant + screen;
            let cached = await getItem(key);
            let retrieve = cached == null || serverNewer(dateValues.user, cached.date) ? true : false;
            if (!retrieve) {
                data = cached.data;
            } else {
                let result = await axios.get('/api/screenusers', { params: { screen: screen } });
                data = result.data;
                await setItem(key, data);
            }
        }
        return data;
    },

    authOptions() {
        let authTypes = [
            { value: common.auth.azure, label: 'Azure' },
            { value: common.auth.DB, label: 'Internal DB' },
            { value: common.auth.google, label: 'Google' }
        ];
        return authTypes;
    },

    async tenantAuthOptions() {
        let options = [
            { value: common.auth.azure, label: 'Azure' },
            { value: common.auth.DB, label: 'Internal DB' },
            { value: common.auth.google, label: 'Google' }
        ];
        let authTypes = [];
        const result = await axios.get('/api/user/tenantauthoptions');
        for (let row of result.data) {
            let option = options.find(x => x.value === row);
            if (option != null) {
                authTypes.push(option);
            }
        }
        return authTypes;
    }
}

async function getCurrencyValues() {
    const key = 'currencies';
    let dataObj = await getItem(key);
    let json = dataObj != null ? dataObj.data : null;

    if (json == null) {
        json = [];
        for (let key of Object.keys(common.currencies)) {
            json.push({ value: key, label: common.currencies[key].code, symbol: common.currencies[key].symbol, name: common.currencies[key].name });
        }
        json.sort((a, b) => a.label > b.label ? 1 : -1);
        await setItem(key, json);
    }
    return json;
}

async function getRooms(serverDate, type, tenant) {
    let obj = null;
    switch (type) {
        case common.cacheValues.room:
            obj = await getItem('allRooms' + tenant);
            break;
        case common.cacheValues.growingRoom:
            obj = await getItem('growingRooms' + tenant);
            break;
        case common.cacheValues.processingRoom:
            obj = await getItem('processingRooms' + tenant);
            break;
        case common.cacheValues.storaggeRoom:
            obj = await getItem('storageRooms' + tenant);
            break;
        case common.cacheValues.defaultRoom:
            obj = await getItem('defaultLocationRooms' + tenant);
            break;
        case common.cacheValues.defaultGrowingRoom:
            obj = await getItem('defaultGrowingRooms' + tenant);
            break;
        case common.cacheValues.defaultProcessingRoom:
            obj = await getItem('defaultProcessingRooms' + tenant);
            break
        case common.cacheValues.defaultStorageRoom:
            obj = await getItem('defaultStorageRooms' + tenant);
            break;
        default:
            break;
    }
    let retrieve = false;
    let json = obj != null ? obj.data : null;
    if (json == null) {
        retrieve = true;
    }
    if (!retrieve) {
        retrieve = serverNewer(serverDate, obj.date);
    }
    if (retrieve) {
        let result = await axios.get('/api/room/selectalllists');
        await setItem('allRooms' + tenant, result.data.all);
        await setItem('growingRooms' + tenant, result.data.growing);
        await setItem('processingRooms' + tenant, result.data.processing);
        await setItem('storageRooms' + tenant, result.data.storage);
        await setItem('defaultLocationRooms' + tenant, result.data.defaults);
        await setItem('defaultGrowingRooms' + tenant, result.data.defaultGrowing);
        await setItem('defaultProcessingRooms' + tenant, result.data.defaultProcessing);
        await setItem('defaultStorageRooms' + tenant, result.data.defaultStorage);
        switch (type) {
            case common.cacheValues.room:
                json = result.data.all;
                break;
            case common.cacheValues.growingRoom:
                json = result.data.growing;
                break;
            case common.cacheValues.processingRoom:
                json = result.data.processing;
                break;
            case common.cacheValues.storaggeRoom:
                json = result.data.storage;
                break;
            case common.cacheValues.defaultRoom:
                json = result.data.defaults;
                break;
            case common.cacheValues.defaultGrowingRoom:
                json = result.data.defaultGrowing;
                break;
            case common.cacheValues.defaultProcessingRoom:
                json = result.data.defaultProcessing;
                break
            case common.cacheValues.defaultStorageRoom:
                json = result.data.defaultStorage;
                break;
            default:
                break;
        }
    }
    return json;
}

async function getLocations(serverDate, locType, tenant) {
    let dataObj = null;
    switch (locType) {
        case common.cacheValues.location:
            dataObj = await getItem('allLocations' + tenant);
            break;
        case common.cacheValues.cultivationLocation:
            dataObj = await getItem('cultivationLocations' + tenant);
            break;
        case common.cacheValues.processingLocation:
            dataObj = await getItem('processingLocations' + tenant);
            break;
        case common.cacheValues.warehouseLocation:
            dataObj = await getItem('warehouseLocations' + tenant);
            break;
        case common.cacheValues.workLocation:
            dataObj = await getItem('workLocations' + tenant);
            break;
        case common.cacheValues.activeLocation:
            dataObj = await getItem('activeLocations' + tenant);
            break;
        case common.cacheValues.cultivationWarehouseLocation:
            dataObj = await getItem('cultivatioWarehouseLocations' + tenant);
            break;
        case common.cacheValues.processingWarehouseLocation:
            dataObj = await getItem('processingWarehouseLocations' + tenant);
            break;
        default:
            return;
    }
    let retrieve = false
    let json = dataObj != null ? dataObj.data : null;
    if (json == null) {
        retrieve = true;
    }
    if (!retrieve) {
        retrieve = serverNewer(serverDate, dataObj.date);
    }
    if (retrieve) {
        let result = await axios.get('/api/location/selectall');
        await setItem('allLocations' + tenant, result.data.all);
        await setItem('cultivationLocations' + tenant, result.data.cultivatio);
        await setItem('processingLocations' + tenant, result.data.processing);
        await setItem('warehouseLocations' + tenant, result.data.warehouse);
        await setItem('workLocations' + tenant, result.data.working);
        await setItem('activeLocations' + tenant, result.data.active);
        await setItem('cultivatioWarehouseLocations' + tenant, result.data.cultWarehouse);
        await setItem('processingWarehouseLocations' + tenant, result.data.procWarehouse);
        switch (locType) {
            case common.cacheValues.location:
                json = result.data.all;
                break;
            case common.cacheValues.cultivationLocation:
                json = result.data.cultivation;
                break;
            case common.cacheValues.processingLocation:
                json = result.data.processing;
                break;
            case common.cacheValues.warehouseLocation:
                json = result.data.warehouse;
                break;
            case common.cacheValues.workLocation:
                json = result.data.working;
                break;
            case common.cacheValues.activeLocation:
                json = result.data.active;
                break;
            case common.cacheValues.cultivationWarehouseLocation:
                json = result.data.cultWarehouse;
                break;
            case common.cacheValues.processingWarehouseLocation:
                json = result.data.procWarehouse;
                break;
            default:
                return;
        }
    }
    return json;
}

async function getValues(serverDate, objName, endpoint, tenant) {
    const key = tenant != null ? objName + tenant : objName;
    let dataObj = await getItem(key);
    let json = dataObj != null ? dataObj.data : null;

    let cacheDateRaw = dataObj != null ? dataObj.date : null;
    let retrieve = false
    // '' is what an empty response body deserializes to. Treat it as a cache miss,
    // otherwise a value type that wasn't seeded yet stays empty for good once its
    // blank response is cached.
    if (json == null || json === '') {
        retrieve = true;
    }
    if (!retrieve && cacheDateRaw != null && serverDate != null) {
        retrieve = serverNewer(serverDate, cacheDateRaw);
    }
    if (retrieve) {
        let data = await axios.get('/api/' + endpoint, { params: { tenant: tenant } });
        json = data.data;
        await setItem(key, json);
    }
    return json;
}

async function _getAdminValues(endpoint, tenant) {
    let json = {};
    let data = null;
    if (tenant != null && tenant !== '') {
        data = await axios.get('/api/' + endpoint + '?tenant=' + tenant);
    } else {
        data = await axios.get('/api/' + endpoint);
    }
    json = data.data;
    return json;
}

function serverNewer(serverDate, cacheDate) {
    let serverRealDate = serverDate != null && serverDate !== '' ? Date.parse(serverDate) : null;
    let cacheRealDate = cacheDate != null && cacheDate !== '' ? Date.parse(cacheDate) : null;
    let result = cacheRealDate == null || serverRealDate == null ? true : serverRealDate > cacheRealDate ? true : false;
    return result;
}

async function setItem(name, value) {
    try {
        // Use put instead of delete+add to avoid race conditions
        await db.cache.put({ id: name, date: new Date().toISOString(), data: value });
    } catch (err) {
        console.error('setItem ' + name);
        console.error(err);
    }
}

async function getItem(name) {
    let value = await db.cache.where({ id: name }).first();
    return value;
}

// Tenant on the current JWT, used to scope cache keys when the caller has no
// tenant to hand. Every db.cache key must be tenant-qualified or it leaks the
// previous tenant's data; sessionStorage 'user' is the decoded token.
function currentTenant() {
    try {
        const raw = sessionStorage.getItem('user');
        return raw != null ? (JSON.parse(raw).tenant ?? null) : null;
    } catch (err) {
        console.error('currentTenant', err);
        return null;
    }
}

// /api/tenantupdateddates memoised for the page. Callers that fetch a batch of
// values (getCachedValues, screenUsers) request it once and amortise it over the
// batch; getEquipmentByMachineType is called per equipment row, so without this
// a single record hydrate would fire one dates request per row.
let updatedDates = null;
let updatedDatesAt = 0;
const UPDATED_DATES_TTL = 30000;

async function tenantUpdatedDates() {
    const now = Date.now();
    if (updatedDates != null && now - updatedDatesAt < UPDATED_DATES_TTL) {
        return updatedDates;
    }
    try {
        const res = await axios.get('/api/tenantupdateddates');
        updatedDates = res?.data ?? {};
        updatedDatesAt = now;
    } catch (err) {
        console.error('tenantUpdatedDates', err);
        updatedDates = updatedDates ?? {};
    }
    return updatedDates;
}

export default valueHelper;
